import { useMemo, useState, type ReactNode } from 'react';
import { canManageTickets, canManageUsers, canManageWebsite, type SessionInfo } from '@avh/shared';
import { SessionContext } from '@/contexts/Session';
import { readStorage, writeStorage } from '@/lib/storage';

const STORAGE_KEY = 'avh.session';

function loadSession(): SessionInfo | null {
  const raw = readStorage(STORAGE_KEY);
  if (!raw) {
    return null;
  }
  try {
    const session = JSON.parse(raw) as SessionInfo;
    return Date.parse(session.expiresAt) > Date.now() ? session : null;
  } catch {
    return null;
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<SessionInfo | null>(loadSession);

  const value = useMemo(
    () => ({
      session,
      canManageTickets: canManageTickets(session?.roles ?? []),
      canManageWebsite: canManageWebsite(session?.roles ?? []),
      canManageUsers: canManageUsers(session?.roles ?? []),
      signIn: (next: SessionInfo) => {
        writeStorage(STORAGE_KEY, JSON.stringify(next));
        setSession(next);
      },
      signOut: () => {
        writeStorage(STORAGE_KEY, null);
        setSession(null);
      },
    }),
    [session],
  );

  return <SessionContext value={value}>{children}</SessionContext>;
}
